import { useState } from "react";
import type { AdminGameDetail } from "@/types";

type FieldChoice = "source" | "target";

interface PickableField {
  key: string;
  label: string;
  type: "text" | "number" | "image" | "longtext" | "tags" | "boolean";
}

const PICKABLE_FIELDS: PickableField[] = [
  { key: "title", label: "Pavadinimas", type: "text" },
  { key: "yearPublished", label: "Metai", type: "number" },
  { key: "imageUrl", label: "Viršelis", type: "image" },
  { key: "descriptionEn", label: "Aprašymas (EN)", type: "longtext" },
  { key: "descriptionLt", label: "Aprašymas (LT)", type: "longtext" },
  { key: "minPlayers", label: "Min. žaidėjų", type: "number" },
  { key: "maxPlayers", label: "Max. žaidėjų", type: "number" },
  { key: "minPlaytime", label: "Min. trukmė", type: "number" },
  { key: "maxPlaytime", label: "Max. trukmė", type: "number" },
  { key: "complexity", label: "Sudėtingumas", type: "number" },
  { key: "minAge", label: "Min. amžius", type: "number" },
  { key: "mechanics", label: "Mechanikos", type: "tags" },
  { key: "genres", label: "Žanrai", type: "tags" },
  { key: "categories", label: "Kategorijos", type: "tags" },
  { key: "families", label: "Šeimos", type: "tags" },
  { key: "designersList", label: "Dizaineriai", type: "tags" },
  { key: "publishersList", label: "Leidėjai", type: "tags" },
  { key: "artists", label: "Dailininkai", type: "tags" },
  { key: "expansions", label: "Plėtiniai", type: "tags" },
  { key: "boxLength", label: "Dėžės ilgis", type: "number" },
  { key: "boxWidth", label: "Dėžės plotis", type: "number" },
  { key: "boxHeight", label: "Dėžės aukštis", type: "number" },
  { key: "isExpansion", label: "Yra plėtinys", type: "boolean" },
  { key: "languageIndependent", label: "Nepriklausomas nuo kalbos", type: "boolean" },
];

// Map camelCase keys to snake_case for the API
const CAMEL_TO_SNAKE: Record<string, string> = {
  yearPublished: "year_published",
  imageUrl: "image_url",
  thumbnailUrl: "thumbnail_url",
  descriptionEn: "description_en",
  descriptionEnSource: "description_en_source",
  descriptionLt: "description_lt",
  descriptionLtSource: "description_lt_source",
  minPlayers: "min_players",
  maxPlayers: "max_players",
  minPlaytime: "min_playtime",
  maxPlaytime: "max_playtime",
  minAge: "min_age",
  isExpansion: "is_expansion",
  languageIndependent: "language_independent",
  designersList: "designers_list",
  publishersList: "publishers_list",
  boxLength: "box_length",
  boxWidth: "box_width",
  boxHeight: "box_height",
};

function getVal(game: AdminGameDetail, key: string): unknown {
  return (game as unknown as Record<string, unknown>)[key];
}

function isEmpty(val: unknown): boolean {
  if (val == null) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  if (Array.isArray(val) && val.length === 0) return true;
  return false;
}

function valuesAreDifferent(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return JSON.stringify(a.sort()) !== JSON.stringify(b.sort());
  }
  return String(a) !== String(b);
}

interface MergeFieldPickerProps {
  sourceGame: AdminGameDetail;
  targetGame: AdminGameDetail;
  onConfirm: (fieldOverrides: Record<string, string>) => void;
  onBack: () => void;
  isPending: boolean;
}

export function MergeFieldPicker({
  sourceGame,
  targetGame,
  onConfirm,
  onBack,
  isPending,
}: MergeFieldPickerProps) {
  // Find conflicting fields: both non-blank and different
  const conflictingFields = PICKABLE_FIELDS.filter((field) => {
    const sourceVal = getVal(sourceGame, field.key);
    const targetVal = getVal(targetGame, field.key);
    return !isEmpty(sourceVal) && !isEmpty(targetVal) && valuesAreDifferent(sourceVal, targetVal);
  });

  const [choices, setChoices] = useState<Record<string, FieldChoice>>(() => {
    const initial: Record<string, FieldChoice> = {};
    for (const field of conflictingFields) {
      initial[field.key] = "target";
    }
    return initial;
  });

  const handleConfirm = () => {
    const overrides: Record<string, string> = {};
    for (const [key, choice] of Object.entries(choices)) {
      if (choice === "source") {
        const snakeKey = CAMEL_TO_SNAKE[key] || key;
        overrides[snakeKey] = "source";
        // Also include source for paired fields
        if (key === "descriptionEn") overrides["description_en_source"] = "source";
        if (key === "descriptionLt") overrides["description_lt_source"] = "source";
      }
    }
    onConfirm(overrides);
  };

  if (conflictingFields.length === 0) {
    // No conflicts — merge immediately
    onConfirm({});
    return null;
  }

  return (
    <div>
      <p className="mb-4 text-sm text-gray-600">
        Abu žaidimai turi skirtingas reikšmes šiuose laukuose. Pasirinkite, kurią
        reikšmę palikti:
      </p>

      <div className="mb-3 grid grid-cols-[1fr_auto_1fr] gap-2 text-xs font-medium uppercase tracking-wider text-gray-500">
        <div className="text-center">
          Sujungiamas (ištrinamas)
        </div>
        <div />
        <div className="text-center">Likantis</div>
      </div>

      <div className="max-h-[50vh] space-y-3 overflow-y-auto">
        {conflictingFields.map((field) => (
          <FieldRow
            key={field.key}
            field={field}
            sourceGame={sourceGame}
            targetGame={targetGame}
            choice={choices[field.key]}
            onChange={(c) => setChoices((prev) => ({ ...prev, [field.key]: c }))}
          />
        ))}
      </div>

      <div className="mt-4 flex justify-between">
        <button
          onClick={onBack}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          Atgal
        </button>
        <button
          onClick={handleConfirm}
          disabled={isPending}
          className="rounded-lg bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          Sujungti
        </button>
      </div>
    </div>
  );
}

function FieldRow({
  field,
  sourceGame,
  targetGame,
  choice,
  onChange,
}: {
  field: PickableField;
  sourceGame: AdminGameDetail;
  targetGame: AdminGameDetail;
  choice: FieldChoice;
  onChange: (c: FieldChoice) => void;
}) {
  const sourceVal = getVal(sourceGame, field.key);
  const targetVal = getVal(targetGame, field.key);

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 rounded-lg border border-gray-200 p-3">
      {/* Source value */}
      <button
        type="button"
        onClick={() => onChange("source")}
        className={`cursor-pointer rounded-lg border-2 p-2 text-left transition-colors ${
          choice === "source"
            ? "border-primary-500 bg-primary-50"
            : "border-transparent hover:border-gray-300"
        }`}
      >
        <FieldValue field={field} value={sourceVal} />
      </button>

      {/* Label */}
      <div className="flex items-center">
        <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
          {field.label}
        </span>
      </div>

      {/* Target value */}
      <button
        type="button"
        onClick={() => onChange("target")}
        className={`cursor-pointer rounded-lg border-2 p-2 text-left transition-colors ${
          choice === "target"
            ? "border-primary-500 bg-primary-50"
            : "border-transparent hover:border-gray-300"
        }`}
      >
        <FieldValue field={field} value={targetVal} />
      </button>
    </div>
  );
}

function FieldValue({ field, value }: { field: PickableField; value: unknown }) {
  if (field.type === "image") {
    return value ? (
      <img
        src={String(value)}
        alt=""
        className="h-20 w-20 rounded-lg object-cover"
      />
    ) : (
      <span className="text-xs text-gray-400">-</span>
    );
  }

  if (field.type === "tags" && Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((tag, i) => (
          <span
            key={i}
            className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
          >
            {tag}
          </span>
        ))}
      </div>
    );
  }

  if (field.type === "longtext") {
    const text = String(value ?? "");
    return (
      <p className="line-clamp-3 text-xs text-gray-700">
        {text.length > 200 ? text.slice(0, 200) + "..." : text}
      </p>
    );
  }

  if (field.type === "boolean") {
    return (
      <span className="text-xs text-gray-700">{value ? "Taip" : "Ne"}</span>
    );
  }

  return (
    <span className="text-sm text-gray-900">{String(value ?? "")}</span>
  );
}
